import { useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import type { SubscriptionAnalyticsBreakdownItem } from "@/types/subscription";
import { Plan, SubscriptionCycle } from "@/types/enums";

interface SubscriptionPlanCardsProps {
  breakdown: SubscriptionAnalyticsBreakdownItem[];
}

interface PlanCounts {
  monthly: number;
  yearly: number;
}

const BILLED_PLANS: Plan[] = [Plan.PLUS, Plan.PRO, Plan.PREMIUM];
const TOTAL_ONLY_PLANS: Plan[] = [Plan.PRO_TRIAL, Plan.STUDENT_PASS];

const PLAN_LABEL: Record<Plan, string> = {
  [Plan.FREE]: "Free",
  [Plan.PLUS]: "Plus",
  [Plan.PRO]: "Pro",
  [Plan.PREMIUM]: "Premium",
  [Plan.PRO_TRIAL]: "Pro Trial",
  [Plan.STUDENT_PASS]: "Student Pass",
};

function buildPlanCounts(
  breakdown: SubscriptionAnalyticsBreakdownItem[],
): Map<Plan, PlanCounts> {
  const map = new Map<Plan, PlanCounts>();
  for (const plan of [...BILLED_PLANS, ...TOTAL_ONLY_PLANS]) {
    map.set(plan, { monthly: 0, yearly: 0 });
  }
  for (const row of breakdown) {
    const entry = map.get(row.plan);
    if (!entry) continue;
    if (row.subscriptionCycle === SubscriptionCycle.MONTHLY) {
      entry.monthly += row.count;
    } else {
      entry.yearly += row.count;
    }
  }
  return map;
}

interface PlanCardProps {
  plan: Plan;
  counts: PlanCounts;
  totalOnly: boolean;
}

function PlanCard({ plan, counts, totalOnly }: PlanCardProps) {
  const total = counts.monthly + counts.yearly;

  return (
    <Card className="pt-0 pb-0">
      <CardContent className="pt-5 pb-4">
        <div className="text-lg font-bold tracking-tight">
          {PLAN_LABEL[plan]}
        </div>
        {totalOnly ? (
          <div className="mt-3">
            <div className="text-3xl font-bold tabular-nums tracking-tight">
              {total.toLocaleString()}
            </div>
            <p className="mt-1 text-sm text-muted-foreground">총 인원</p>
          </div>
        ) : (
          <div className="mt-3 space-y-2">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm font-medium text-muted-foreground">
                월간
              </span>
              <span className="text-xl font-bold tabular-nums tracking-tight">
                {counts.monthly.toLocaleString()}
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm font-medium text-muted-foreground">
                연간
              </span>
              <span className="text-xl font-bold tabular-nums tracking-tight">
                {counts.yearly.toLocaleString()}
              </span>
            </div>
            <div className="h-px bg-border" />
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm font-semibold">총</span>
              <span className="text-xl font-bold tabular-nums tracking-tight">
                {total.toLocaleString()}
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function SubscriptionPlanCards({
  breakdown,
}: SubscriptionPlanCardsProps) {
  const counts = useMemo(() => buildPlanCounts(breakdown), [breakdown]);

  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
      {BILLED_PLANS.map((plan) => (
        <PlanCard
          key={plan}
          plan={plan}
          counts={counts.get(plan) ?? { monthly: 0, yearly: 0 }}
          totalOnly={false}
        />
      ))}
      {TOTAL_ONLY_PLANS.map((plan) => (
        <PlanCard
          key={plan}
          plan={plan}
          counts={counts.get(plan) ?? { monthly: 0, yearly: 0 }}
          totalOnly
        />
      ))}
    </div>
  );
}
